import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import type { NetworkConnection, NetworkReadiness, IngestReport } from '../services/apiService';

// The Networks page: a publisher's accounts at affiliate networks, as a LIST.
//
// Several at once is the normal case — Awin for one merchant set, CJ for
// another, 2Performant for the Romanian ones — so this grows over time rather
// than being a single choice made once at signup.
//
// A CONNECTION IS COMPLETE WITH A FEED ALONE. Conversion tracking is an upgrade
// offered afterwards, so "not set up" renders as an OFFER beside a working feed,
// never as a warning: a publisher who declines keeps a fully working product
// with estimated rather than measured earnings.

const NETWORK_LABELS: Record<string, string> = {
  '2performant': '2Performant',
  awin: 'Awin',
  cj: 'CJ Affiliate',
  rakuten: 'Rakuten Advertising',
  impact: 'Impact'
};

/**
 * What a network costs a publisher in accuracy, said on the page where they see
 * its numbers.
 *
 * Rakuten's is not a caveat we could avoid by trying harder: they keep click
 * data for 30 days, so older conversions genuinely arrive untagged. An
 * unexplained gap in someone's attributed earnings reads as a broken install,
 * and they would go looking for a fault that is not there.
 */
const NETWORK_CAVEATS: Record<string, string> = {
  rakuten:
    'Rakuten keeps click data for 30 days. Conversions from older clicks still count '
    + 'towards your earnings, but arrive without our tag and cannot be traced to a feed. '
    + 'Rakuten also never reports approval, so these stay marked pending.'
};

/**
 * What the credential form must know per network. This mirrors the route's own
 * rules (accountRef required except 2Performant, colon-joined composites) so a
 * publisher learns the shape BEFORE a failed submit rather than from a 400.
 */
const CREDENTIAL_FIELDS: Record<string, {
  accountLabel?: string;
  credentialLabel: string;
  credentialHint?: string;
  wantsCurrency?: boolean;
}> = {
  awin: {
    accountLabel: 'Awin publisher ID',
    credentialLabel: 'API token'
  },
  cj: {
    accountLabel: 'CJ company ID (CID)',
    credentialLabel: 'Personal access token',
    // CJ reports amounts with no currency code at all, so the publisher tells
    // us once per account rather than us guessing per row.
    wantsCurrency: true
  },
  rakuten: {
    accountLabel: 'Rakuten publisher SID',
    credentialLabel: 'Client ID and secret',
    credentialHint: 'Joined with a colon: clientId:clientSecret'
  },
  impact: {
    accountLabel: 'Impact account SID',
    credentialLabel: 'Auth token',
    credentialHint: 'sid:token, or just the token'
  },
  '2performant': {
    credentialLabel: 'User key and account login',
    credentialHint:
      '2Performant offers no API-only access, so this is your user key, account email and '
      + 'password, joined with colons: userKey:email:password. The user key is in your '
      + '2Performant settings under "User key header". It is encrypted and never shown again.'
  }
};

const cardStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '1rem',
  padding: '1rem',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  marginBottom: '0.75rem'
};

const fieldStyle: React.CSSProperties = { display: 'block', marginBottom: '0.5rem', width: '100%' };

/**
 * What we say about the measurement half of a connection.
 *
 * `reconnect_required` is the ONLY fault here, and it earns strong wording:
 * every credential dies silently — a token revoked in a dashboard we never see,
 * a password changed — and unless we say so, all of those present to the
 * publisher as "you stopped earning".
 */
function measurementLabel(conn: NetworkConnection, readiness?: NetworkReadiness) {
  if (conn.syncState === 'reconnect_required') {
    return { text: 'Reconnect needed — earnings are not updating', tone: 'var(--danger)' };
  }
  if (conn.syncState === 'active') {
    return { text: 'Earnings measured', tone: 'var(--success, green)' };
  }
  if (readiness?.state === 'satisfied') {
    return { text: 'Earnings measured from your uploads', tone: 'var(--success, green)' };
  }
  if (readiness?.state === 'expected') {
    return {
      text: `${readiness.clicks} clicks in the last 30 days we cannot follow`,
      tone: 'var(--warning, #a76a00)'
    };
  }
  return { text: 'Earnings tracking — not set up', tone: 'var(--muted)' };
}

function reportSummary(report?: IngestReport): string {
  if (!report) return '';
  const bits = [`read ${report.rowsRead} rows`, `${report.matched} matched to your clicks`];
  // Read off the report rather than a second argument. Both call sites passed
  // only the report, so the rejected count was never rendered — a publisher
  // whose file had unusable rows was told the row count and nothing about them.
  if (report.rejected) bits.push(`${report.rejected} could not be used`);
  return bits.join(', ');
}

/**
 * The setup panel behind the call to action — a credential form.
 *
 * The credential submit legitimately takes seconds: the API verifies against
 * the live network and runs a first sweep before answering, so an empty page
 * afterwards never reads as "it did not take".
 */
const SetupPanel: React.FC<{
  conn: NetworkConnection;
  onDone: () => void;
}> = ({ conn, onDone }) => {
  const fields = CREDENTIAL_FIELDS[conn.network];
  const [credential, setCredential] = useState('');
  const [accountRef, setAccountRef] = useState('');
  const [currency, setCurrency] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submitCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setBusy(true);
    try {
      const result = await apiService.connectNetworkCredential(conn.id, {
        credential,
        accountRef: accountRef || undefined,
        defaultCurrency: currency || undefined
      });
      if (!result.success) {
        setMessage(result.message || 'Could not save that credential.');
        return;
      }
      // The caveat is said NOW, while they are looking at the screen they just
      // used — discovered later, from a number that does not add up, it costs
      // trust that saying it up front does not.
      const summary = result.synced && result.report
        ? `Connected — first sweep ${reportSummary(result.report)}.`
        : 'Connected. The first sweep will run shortly.';
      setSuccess(result.caveat ? `${summary} ${result.caveat}` : summary);
      onDone();
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <p data-testid={`network-${conn.network}-connected`} style={{ flexBasis: '100%', margin: 0 }}>
        {success}
      </p>
    );
  }

  return (
    <div style={{ flexBasis: '100%' }} data-testid={`network-${conn.network}-setup`}>
      {fields && (
        <form onSubmit={submitCredential} style={{ maxWidth: '28rem' }}>
          {fields.accountLabel && (
            <label style={fieldStyle}>
              {fields.accountLabel}
              <input
                type="text"
                value={accountRef}
                onChange={e => setAccountRef(e.target.value)}
                required
                data-testid={`network-${conn.network}-account-ref`}
                style={{ width: '100%' }}
              />
            </label>
          )}
          <label style={fieldStyle}>
            {fields.credentialLabel}
            <input
              type="password"
              value={credential}
              onChange={e => setCredential(e.target.value)}
              required
              data-testid={`network-${conn.network}-credential`}
              style={{ width: '100%' }}
            />
          </label>
          {fields.credentialHint && (
            <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: 0 }}>
              {fields.credentialHint}
            </p>
          )}
          {fields.wantsCurrency && (
            <label style={fieldStyle}>
              Currency your commissions are reported in
              <input
                type="text"
                value={currency}
                onChange={e => setCurrency(e.target.value)}
                placeholder="USD"
                maxLength={3}
                data-testid={`network-${conn.network}-currency`}
                style={{ width: '6rem', display: 'block' }}
              />
            </label>
          )}
          <button type="submit" className="btn-primary" disabled={busy || !credential} data-testid={`network-${conn.network}-credential-submit`}>
            {busy ? 'Checking with the network…' : (conn.syncState === 'reconnect_required' ? 'Reconnect' : 'Connect')}
          </button>
          {message && (
            <p role="alert" data-testid={`network-${conn.network}-credential-error`} style={{ color: 'var(--danger)' }}>
              {message}
            </p>
          )}
        </form>
      )}
    </div>
  );
};

const NetworksPage: React.FC = () => {
  const [connections, setConnections] = useState<NetworkConnection[] | null>(null);
  const [readiness, setReadiness] = useState<NetworkReadiness[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [openSetup, setOpenSetup] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [conns, ready] = await Promise.all([
        apiService.getNetworkConnections(),
        // Readiness is an enhancement to the list, not a prerequisite for it:
        // failing to compute it must not hide the publisher's own networks.
        apiService.getMeasurementReadiness().catch(() => [] as NetworkReadiness[])
      ]);
      setConnections(conns);
      setReadiness(ready);
    } catch (err) {
      console.error('Failed to load networks:', err);
      setError('Could not load your networks.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (connectionId: number) => {
    try {
      await apiService.dismissMeasurementOffer(connectionId);
      await load();
    } catch (err) {
      console.error('Failed to dismiss measurement offer:', err);
    }
  };

  if (error) {
    return (
      <section className="stats-section">
        <h2>Your networks</h2>
        <p data-testid="networks-error">{error}</p>
        <button onClick={load}>Retry</button>
      </section>
    );
  }

  if (!connections) {
    return <section className="stats-section"><h2>Your networks</h2><p>Loading…</p></section>;
  }

  return (
    <section className="stats-section" data-testid="networks-page">
      <h2>Your networks</h2>
      <p style={{ color: 'var(--muted)' }}>
        Each network you work with holds your product feed, and optionally the connection
        that lets us measure what your overlays actually earn.
      </p>

      {/* The old copy read "connect a product feed and one will appear here",
          which is false for a publisher who HAS connected one: ensureConnection
          returns null for a network that can never carry conversions (a generic
          or Amazon-format CSV has no commissions API), so an uploaded feed can
          be working perfectly and this page still says nothing is here. Say
          which networks qualify, and give it a door — this page is reachable
          from two CTAs and no nav item, so a dead end here is a dead end. */}
      {connections.length === 0 && (
        <p data-testid="networks-empty">
          Nothing to connect yet. Earnings tracking is available for feeds from Awin,
          Commission Junction, Impact, Rakuten and 2Performant — a generic or
          Amazon-format CSV carries no commissions to read back.{' '}
          <Link to="/feeds/add">Add a network feed</Link> and it will appear here.
        </p>
      )}

      {connections.map(conn => {
        const ready = readiness.find(r => r.network === conn.network);
        const measurement = measurementLabel(conn, ready);
        const setupOpen = openSetup === conn.id;
        // A dismissed offer stays reachable but quiet: the dismissal is a
        // preference stored server-side, not a wall. `expected` out-ranks it
        // (dismissible is false there), and that ordering lives in the API's
        // hysteresis — nothing here re-decides it.
        const offerQuietly = !!ready?.dismissed && ready?.state !== 'expected';
        return (
          <div key={conn.id} style={cardStyle} data-testid={`network-${conn.network}`}>
            <div>
              <div style={{ fontWeight: 600 }}>{NETWORK_LABELS[conn.network] || conn.network}</div>
              <div style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
                {conn.feedCount} {conn.feedCount === 1 ? 'feed' : 'feeds'}
                {' · '}
                <span style={{ color: measurement.tone }} data-testid={`network-${conn.network}-measurement`}>
                  {measurement.text}
                </span>
              </div>
            </div>
            {NETWORK_CAVEATS[conn.network] && (
              <p
                data-testid={`network-${conn.network}-caveat`}
                style={{ flexBasis: '100%', margin: 0, fontSize: '0.85rem', color: 'var(--muted)' }}
              >
                {NETWORK_CAVEATS[conn.network]}
              </p>
            )}
            {conn.syncState !== 'active' && !setupOpen && (
              <span style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => setOpenSetup(conn.id)}
                  data-testid={`network-${conn.network}-cta`}
                  className={offerQuietly ? 'btn btn--secondary' : 'btn btn--primary'}
                >
                  {conn.syncState === 'reconnect_required' ? 'Reconnect' : 'Set up earnings tracking'}
                </button>
                {ready?.dismissible && !ready.dismissed && ready.state !== 'satisfied' && (
                  <button
                    type="button"
                    onClick={() => dismiss(conn.id)}
                    data-testid={`network-${conn.network}-dismiss`}
                    style={{ fontSize: '0.85rem', color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Not now
                  </button>
                )}
              </span>
            )}
            {setupOpen && <SetupPanel conn={conn} onDone={load} />}
          </div>
        );
      })}
    </section>
  );
};

export default NetworksPage;
